/**
 * NULL-aware sums and ratios for /overview. A metric no row reported stays
 * null (never 0); a ratio divides sums (never averages ratios) and is null
 * over a null or 0 denominator. Pure.
 *
 * @module services/overview/overviewMath
 */
import { BENCHMARK_MIN_IMPRESSIONS } from '@/services/insights/insightConstants';
import type { FactRow, Totals } from './overviewTypes';

/** NULL stays NULL: never `Number(null)` (= 0). A non-numeric value is a broken cell, not a zero. */
export const nullableNumber = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** null + x = x; null + null = null. */
export const addNullable = (a: number | null, b: number | null): number | null =>
  (a === null ? b : b === null ? a : a + b);

const NO_TOTALS: Totals = { spend: null, impressions: null, clicks: null, conversions: null, conversionValue: null, reach: null };

export const totalsOf = (rows: readonly FactRow[]): Totals =>
  rows.reduce<Totals>((t, r) => ({
    spend: addNullable(t.spend, r.spend),
    impressions: addNullable(t.impressions, r.impressions),
    clicks: addNullable(t.clicks, r.clicks),
    conversions: addNullable(t.conversions, r.conversions),
    conversionValue: addNullable(t.conversionValue, r.conversionValue),
    reach: addNullable(t.reach, r.reach),
  }), NO_TOTALS);

/** num × scale ÷ den; null over a null or 0 denominator, and over a null numerator. */
export const ratioOf = (num: number | null, den: number | null, scale = 1): number | null =>
  (num === null || den === null || den === 0 ? null : (num * scale) / den);

export const ctrOf = (t: Totals): number | null => ratioOf(t.clicks, t.impressions);
export const cpcOf = (t: Totals): number | null => ratioOf(t.spend, t.clicks);
export const cpmOf = (t: Totals): number | null => ratioOf(t.spend, t.impressions, 1000);

/** R17: fewer than 500 impressions fades the ratios; unreported impressions is not a small sample. */
export const isLowSample = (t: Pick<Totals, 'impressions'>): boolean =>
  t.impressions !== null && t.impressions < BENCHMARK_MIN_IMPRESSIONS;

/** Rows grouped by a key, in first-seen order. */
export const groupRows = <R>(rows: readonly R[], keyOf: (r: R) => string): Array<[string, R[]]> => {
  // Built locally and never shared; copying per row would be quadratic.
  const groups = new Map<string, R[]>();
  rows.forEach((r) => {
    const k = keyOf(r);
    const g = groups.get(k);
    if (g) g.push(r);
    else groups.set(k, [r]);
  });
  return [...groups.entries()];
};
